import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, RefreshCw, Search, Settings2, Wifi, WifiOff, Megaphone, Clock, Check } from 'lucide-react';
import clsx from 'clsx';
import { useLeaderAgents, STATUS_DISPLAY, fmtClock } from '@/lib/useLeaderAgents';
import { fmtMSS } from '@/lib/useTodayStats';
import { Settings as SettingsApi, type LeaderAgentRow, type AgentLiveStatus } from '@/lib/api';
import { AgentActionsModal } from '@/components/leader/AgentActionsModal';
import { BroadcastModal } from '@/components/leader/BroadcastModal';

/**
 * Takım Lideri → Temsilciler. Eski Dashboard.js agent DataTable'ının modern
 * karşılığı. Canlı durum (3CX xapi) + günlük metrik (activity_logs), 15s polling.
 *
 * Sütunlar: Temsilci · Durum · Kuyruk · Giriş · Cevaplanan · Cevapsız · Mola · Ort.Konuşma
 * Aksiyon: satıra tıkla → uzaktan durum/mola/kuyruk modal'ı.
 */

type FilterKey = 'all' | 'online' | AgentLiveStatus;

export default function LeaderAgents() {
  const { data: agents, isLoading, isFetching, refetch, isError } = useLeaderAgents();
  const [filter, setFilter] = useState<FilterKey>('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<LeaderAgentRow | null>(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  const counts = useMemo(() => {
    const list = agents ?? [];
    return {
      total: list.length,
      online: list.filter((a) => a.online).length,
      hazir: list.filter((a) => a.status === 'hazir').length,
      mola: list.filter((a) => ['mola', 'yemek', 'dis-arama', 'ozel'].includes(a.status)).length,
      cevrimdisi: list.filter((a) => !a.online).length,
    };
  }, [agents]);

  const filtered = useMemo(() => {
    let list = agents ?? [];
    if (filter === 'online') list = list.filter((a) => a.online);
    else if (filter !== 'all') list = list.filter((a) => a.status === filter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter(
        (a) => a.displayName.toLowerCase().includes(needle) || a.extension.includes(needle),
      );
    }
    return list;
  }, [agents, filter, q]);

  return (
    <div className="px-5 py-3.5 max-w-[1280px] mx-auto">
      {/* Header */}
      <header className="flex items-end justify-between mb-3 gap-3">
        <div>
          <h1 className="text-[18px] font-semibold tracking-tight text-slate-900 leading-tight">Temsilciler</h1>
          <div className="text-[11.5px] text-slate-400 mt-1 flex items-center gap-1.5">
            <span>Takım Lideri</span>
            <ChevronRight size={11} className="opacity-60" />
            <span>Temsilciler</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ShiftSetting />
          <button
            onClick={() => setBroadcastOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-brand-500 hover:bg-brand-600 text-[#08140d] shadow-[0_4px_12px_-3px_rgba(255,79,31,0.5)] transition active:scale-[0.97]"
          >
            <Megaphone size={13} /> Toplu Mesaj
          </button>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-surface ring-1 ring-slate-200 text-slate-600 hover:bg-slate-50 transition"
          >
            <RefreshCw size={13} className={clsx(isFetching && 'animate-spin')} /> Yenile
          </button>
        </div>
      </header>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-3">
        <Tile label="Toplam" value={counts.total} tone="slate" active={filter === 'all'} onClick={() => setFilter('all')} />
        <Tile label="Çevrimiçi" value={counts.online} tone="emerald" active={filter === 'online'} onClick={() => setFilter('online')} />
        <Tile label="Molada" value={counts.mola} tone="amber" active={filter === 'mola'} onClick={() => setFilter('mola')} />
        <Tile label="Çevrimdışı" value={counts.cevrimdisi} tone="rose" active={filter === 'cevrimdisi'} onClick={() => setFilter('cevrimdisi')} />
      </div>

      {/* Filter chips + search */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <FilterChip label="Tümü" active={filter === 'all'} onClick={() => setFilter('all')} />
          <FilterChip label="Hazır" active={filter === 'hazir'} onClick={() => setFilter('hazir')} dot="bg-emerald-500" />
          <FilterChip label="Mola" active={filter === 'mola'} onClick={() => setFilter('mola')} dot="bg-amber-500" />
          <FilterChip label="Yemek" active={filter === 'yemek'} onClick={() => setFilter('yemek')} dot="bg-orange-500" />
          <FilterChip label="Dış Arama" active={filter === 'dis-arama'} onClick={() => setFilter('dis-arama')} dot="bg-brand-500" />
          <FilterChip label="Özel" active={filter === 'ozel'} onClick={() => setFilter('ozel')} dot="bg-violet-500" />
          <FilterChip label="Kuyruk Dışı" active={filter === 'kuyruk-disi'} onClick={() => setFilter('kuyruk-disi')} dot="bg-slate-400" />
        </div>
        <div className="ml-auto relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="İsim / dahili ara"
            className="pl-7 pr-3 py-1.5 rounded-lg text-[12px] bg-surface ring-1 ring-slate-200 focus:ring-brand-300 outline-none w-[180px]"
          />
        </div>
      </div>

      {/* Table */}
      <section className="bg-surface border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="px-4 py-2.5 font-medium">Temsilci</th>
                <th className="px-3 py-2.5 font-medium">Durum</th>
                <th className="px-3 py-2.5 font-medium text-center">Kuyruk</th>
                <th className="px-3 py-2.5 font-medium text-center">Giriş</th>
                <th className="px-3 py-2.5 font-medium text-center">Cevaplanan</th>
                <th className="px-3 py-2.5 font-medium text-center">Cevapsız</th>
                <th className="px-3 py-2.5 font-medium text-center">Mola</th>
                <th className="px-3 py-2.5 font-medium text-center">Ort. Konuşma</th>
                <th className="px-3 py-2.5 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400 text-[12.5px]">Yükleniyor…</td></tr>
              )}
              {isError && !isLoading && (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-red-500 text-[12.5px]">Veri alınamadı — 3CX bağlantısını kontrol et</td></tr>
              )}
              {!isLoading && !isError && filtered.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400 text-[12.5px]">
                  {(agents?.length ?? 0) === 0 ? 'Henüz temsilci tanımlı değil' : 'Filtreye uyan temsilci yok'}
                </td></tr>
              )}
              {filtered.map((a) => (
                <AgentRow key={a.id} agent={a} onClick={() => setSelected(a)} />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-[11px] text-slate-400 mt-2.5 text-center">
        Canlı durum 3CX'ten · günlük metrik ccark aktivitesinden · 15 saniyede bir güncellenir
      </p>

      {selected && <AgentActionsModal agent={selected} onClose={() => setSelected(null)} />}
      {broadcastOpen && <BroadcastModal onClose={() => setBroadcastOpen(false)} />}
    </div>
  );
}

/* ─── Row ─────────────────────────────────────────────────────── */

function AgentRow({ agent, onClick }: { agent: LeaderAgentRow; onClick: () => void }) {
  const sd = STATUS_DISPLAY[agent.status];
  return (
    <tr
      onClick={onClick}
      className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 cursor-pointer transition"
    >
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2.5">
          <div className="relative w-8 h-8 rounded-full bg-gradient-to-br from-slate-400 to-slate-500 text-white flex items-center justify-center text-[11px] font-semibold shrink-0">
            {agent.displayName.slice(0, 2).toUpperCase()}
            <span className={clsx(
              'absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-base',
              agent.online ? 'bg-emerald-500' : 'bg-slate-300',
            )} />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate leading-tight">{agent.displayName}</div>
            <div className="text-[11px] text-slate-400 font-mono">{agent.extension}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-2.5">
        <span className={clsx('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ring-1 text-[11px] font-medium', sd.chip)}>
          <span className={clsx('w-1.5 h-1.5 rounded-full', sd.dot)} />
          {sd.label}
          {agent.status === 'ozel' && agent.profileMessage ? ` · ${agent.profileMessage}` : ''}
        </span>
      </td>
      <td className="px-3 py-2.5 text-center">
        {agent.queueLoggedIn
          ? <Wifi size={14} className="text-emerald-500 inline" />
          : <WifiOff size={14} className="text-slate-300 inline" />}
      </td>
      <td className="px-3 py-2.5 text-center text-slate-600 font-mono text-[11.5px]">{fmtClock(agent.daily.firstActivityAt)}</td>
      <td className="px-3 py-2.5 text-center font-medium text-slate-900">{agent.daily.callsAnswered}</td>
      <td className="px-3 py-2.5 text-center text-rose-600">{agent.daily.callsMissed || <span className="text-slate-300">0</span>}</td>
      <td className="px-3 py-2.5 text-center text-slate-600">{agent.daily.breakCount || <span className="text-slate-300">0</span>}</td>
      <td className="px-3 py-2.5 text-center text-slate-600 font-mono text-[11.5px]">{fmtMSS(agent.daily.avgCallSec)}</td>
      <td className="px-3 py-2.5 text-right">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:bg-brand-50 hover:text-brand-600 transition">
          <Settings2 size={14} />
        </span>
      </td>
    </tr>
  );
}

/* ─── Tiles + chips ───────────────────────────────────────────── */

function Tile({ label, value, tone, active, onClick }: {
  label: string; value: number; tone: 'slate' | 'emerald' | 'amber' | 'rose';
  active: boolean; onClick: () => void;
}) {
  const toneCls = {
    slate:   'text-slate-700',
    emerald: 'text-emerald-600',
    amber:   'text-amber-600',
    rose:    'text-rose-600',
  }[tone];
  return (
    <button
      onClick={onClick}
      className={clsx(
        'bg-surface rounded-xl ring-1 px-4 py-3 text-left transition hover:ring-brand-200',
        active ? 'ring-brand-300 shadow-sm' : 'ring-slate-200',
      )}
    >
      <div className={clsx('text-[26px] font-bold leading-none tabular-nums', toneCls)}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400 mt-1">{label}</div>
    </button>
  );
}

function FilterChip({ label, active, onClick, dot }: {
  label: string; active: boolean; onClick: () => void; dot?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-medium ring-1 transition',
        active ? 'bg-brand-500 text-[#08140d] ring-brand-500' : 'bg-surface text-slate-600 ring-slate-200 hover:bg-slate-50',
      )}
    >
      {dot && <span className={clsx('w-1.5 h-1.5 rounded-full', active ? 'bg-surface' : dot)} />}
      {label}
    </button>
  );
}

/* ─── Vardiya ayarı (leader) ───────────────────────────────────── */

function ShiftSetting() {
  const qc = useQueryClient();
  // placeholderData + ?? 12 → ekran asla boş kalmaz, default 12 hemen gelir.
  // staleTime → sayfa değişip dönünce cache'ten anında okunur (boş gelmez).
  const { data } = useQuery({
    queryKey: ['settings'],
    queryFn: SettingsApi.get,
    staleTime: 5 * 60 * 1000,
    placeholderData: { shiftHours: 12 },
  });
  const serverHours = data?.shiftHours ?? 12;

  const [val, setVal] = useState(String(serverHours));
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  // Kullanıcı düzenlemiyorsa sunucu değeriyle senkron tut (dönüşte doğru değer).
  useEffect(() => { if (!dirty) setVal(String(serverHours)); }, [serverHours, dirty]);

  const mut = useMutation({
    mutationFn: (h: number) => SettingsApi.setShiftHours(h),
    onSuccess: (r) => {
      qc.setQueryData(['settings'], { shiftHours: r.shiftHours });
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    },
  });

  const n = Number(val);
  const valid = Number.isInteger(n) && n >= 1 && n <= 24;
  const canSave = dirty && valid && n !== serverHours && !mut.isPending;

  return (
    <div
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-surface ring-1 ring-slate-200 text-[12px] text-slate-600"
      title="Vardiya penceresi — login ve kuyruk süresi bu süre içinde kümülatif sayılır; aşılınca yeni vardiya (sıfırdan)."
    >
      <Clock size={13} className="text-slate-400" />
      <span>Vardiya</span>
      <input
        type="number" min={1} max={24} value={val}
        onChange={(e) => { setVal(e.target.value); setDirty(true); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && canSave) mut.mutate(n); }}
        className="w-9 bg-transparent text-center font-mono text-slate-900 ring-1 ring-slate-200 rounded px-1 py-0.5 focus:ring-brand-300 outline-none"
      />
      <span className="text-slate-400">saat</span>
      <button
        onClick={() => canSave && mut.mutate(n)}
        disabled={!canSave}
        className={clsx(
          'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium transition',
          canSave ? 'bg-brand-500 text-[#08140d] hover:bg-brand-600' : 'bg-slate-100 text-slate-400 cursor-not-allowed',
        )}
      >
        {mut.isPending ? '…' : saved ? <><Check size={11} /> Kayıtlı</> : 'Kaydet'}
      </button>
    </div>
  );
}
